import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector } from "react-redux";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import Preloader from "../../../components/CustomComponents/preLoad/preLoader";
import { Panel, PanelBody, PanelHeader } from "../../../components/panel/panel";
import showErrorToast, { showSuccessToast } from "../../../components/CustomComponents/toast/toast";
import FormInput from "../../../components/CustomComponents/Input/formInput";
import Select from "react-select";
import DropDownStyles, { DropDownStylesDark } from "../../staff/dropdownStyles";
import CountryCitySelector from "../../../components/CustomComponents/DropdownSelections/CountryCitySelector";
import ToggleSwitch from "../../../components/CustomComponents/Switchery/switchery";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";

const UpdateBranch = () => {
  const { branchId } = useParams();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;
  const profileData = useSelector((state) => state.auth.profileData);
  const parentComID = profileData?.parentComID ?? profileData?.ParentComID;
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [currencies, setCurrencies] = useState([]);
  const [timeZones, setTimeZones] = useState([]);

  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;
  const themeColor = useThemeColor();

  const [industryTypes] = useState([
    { value: "Hospitality", label: "Hospitality" },
    { value: "Retail", label: "Retail" },
    { value: "Saloon", label: "Saloon" },
  ]);

  const [formData, setFormData] = useState({
    compName: "",
    compDetail: "",
    compEmail: "",
    compPhone: "",
    compFax: "",
    compIndustryType: "",
    compCurrency: "",
    compCountry: "",
    compCity: "",
    compAddress: "",
    compSecCode: "",
    compType: "",
    compRemarks: "",
    compPNotificationLimit_PerDay: 10,
    compLanguage: "en",
    compWebsite: "",
    compTimeZone: "",
    compUTCTimeZone: "",
    compPublish: true,
  });

  // Store the complete branch data to preserve all fields
  const [completeBranchData, setCompleteBranchData] = useState(null);

  useEffect(() => {
    // Wait until profile is loaded before deciding parent vs branch access
    if (parentComID == null) return;
    if (parentComID !== 0) {
      navigate("/branches");
      return;
    }

    fetchCurrencies();
    fetchTimeZones();
    fetchBranchData();
  }, [branchId, parentComID, navigate]);

  const fetchCurrencies = async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_CURRENCIES);
      if (response.data && response.data.data) {
        setCurrencies(
          response.data.data.map((c) => ({ value: c.value, label: c.text }))
        );
      }
    } catch (err) {
      showErrorToast("Failed to load currencies");
    }
  };

  const fetchTimeZones = async () => {
    try {
      const response = await axiosInstance.get(APIs.GET_ALL_TIMEZONE);
      if (response.data && response.data.data) {
        setTimeZones(response.data.data);
      }
    } catch (err) {
      showErrorToast("Failed to load timezones");
    }
  };

  const fetchBranchData = async () => {
    try {
      setFetching(true);
      const response = await axiosInstance.get(APIs.GET_COMPANY_LOCATIONS, {
        params: { CompID: CompID },
      });

      if (response.data && response.data.data) {
        const branch = response.data.data.find(
          (b) => b.compID === parseInt(branchId)
        );
        if (branch) {
          // Store complete branch data to preserve all fields
          setCompleteBranchData(branch);

          // Set form data for visible fields
          setFormData({
            compName: branch.compName || "",
            compDetail: branch.compDetail || "",
            compEmail: branch.compEmail || "",
            compPhone: branch.compPhone || "",
            compFax: branch.compFax || "",
            compIndustryType: branch.compIndustryType || "",
            compCurrency: branch.compCurrency || "",
            compCountry: branch.compCountry || "",
            compCity: branch.compCity || "",
            compAddress: branch.compAddress || "",
            compSecCode: branch.compSecCode || "",
            compType: branch.compType || "",
            compRemarks: branch.compRemarks || "",
            compPNotificationLimit_PerDay: branch.compPNotificationLimit_PerDay || 10,
            compLanguage: branch.compLanguage || "en",
            compWebsite: branch.compWebsite || "",
            compTimeZone: branch.compTimeZone || "",
            compUTCTimeZone: branch.compUTCTimeZone || "",
            compPublish: branch.compPublish !== undefined ? branch.compPublish : true,
          });
        } else {
          showErrorToast("Branch not found");
          navigate("/branches");
        }
      }
    } catch (err) {
      showErrorToast("Failed to load branch data");
      navigate("/branches");
    } finally {
      setFetching(false);
    }
  };

  const handleInputChange = (field) => (e) => {
    const value = e.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (selectedOption, field) => {
    if (field === "compTimeZone") {
      setFormData({ 
        ...formData, 
        compTimeZone: selectedOption?.value || "",
        compUTCTimeZone: selectedOption?.label || ""
      });
    } else {
      setFormData({ ...formData, [field]: selectedOption?.value || "" });
    }
  };

  const handleCountryCityChange = (data) => {
    setFormData((prev) => ({ ...prev, ...data }));
  };

  const validateForm = () => {
    if (!formData.compName?.trim()) {
      showErrorToast("Branch name is required");
      return false;
    }
    if (!formData.compEmail?.trim()) {
      showErrorToast("Email is required");
      return false;
    }
    if (!formData.compSecCode?.trim()) {
      showErrorToast("Binding code is required");
      return false;
    }
    if (!formData.compTimeZone?.trim()) {
      showErrorToast("Time zone is required");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      
      // Start with complete branch data to preserve all hidden fields (dates, IDs, etc.)
      // Convert camelCase API response to PascalCase payload format
      const payload = completeBranchData ? {
        // Core IDs
        CompID: parseInt(branchId),
        ParentComID: completeBranchData.parentComID || completeBranchData.ParentComID,
        
        // Editable fields from form
        CompName: formData.compName,
        CompDetail: formData.compDetail || "",
        CompEmail: formData.compEmail,
        CompPhone: formData.compPhone || "",
        CompFax: formData.compFax || "",
        CompIndustryType: formData.compIndustryType,
        CompCurrency: formData.compCurrency,
        CompCountry: formData.compCountry || "",
        CompCity: formData.compCity || "",
        CompAddress: formData.compAddress || "",
        CompSecCode: formData.compSecCode,
        CompType: completeBranchData.compType || completeBranchData.CompType || "",
        CompRemarks: formData.compRemarks || "",
        CompPNotificationLimit_PerDay: formData.compPNotificationLimit_PerDay || 10,
        CompLanguage: formData.compLanguage || "en",
        CompWebsite: formData.compWebsite || "",
        CompTimeZone: formData.compTimeZone || "",
        CompUTCTimeZone: formData.compUTCTimeZone || "",
        CompPublish: formData.compPublish !== undefined ? formData.compPublish : true,
        
        // Preserve all hidden fields (dates, licenses, settings, etc.)
        CompRegDate: completeBranchData.compRegDate || completeBranchData.CompRegDate,
        CompExpireDate: completeBranchData.compExpireDate || completeBranchData.CompExpireDate,
        UpdateDateTime: completeBranchData.updateDateTime || completeBranchData.UpdateDateTime,
        CompIcon: completeBranchData.compIcon || completeBranchData.CompIcon,
        CompHeaderLogo: completeBranchData.compHeaderLogo || completeBranchData.CompHeaderLogo,
        CompCoverImage: completeBranchData.compCoverImage || completeBranchData.CompCoverImage,
        CompMaxIPadLicense: completeBranchData.compMaxIPadLicense || completeBranchData.CompMaxIPadLicense,
        CompMaxUserLicense: completeBranchData.compMaxUserLicense || completeBranchData.CompMaxUserLicense,
        LocationLicences: completeBranchData.locationLicences || completeBranchData.LocationLicences,
        CompLatitude: completeBranchData.compLatitude || completeBranchData.CompLatitude,
        CompLongitude: completeBranchData.compLongitude || completeBranchData.CompLongitude,
        IsNeedtoSyncDB: completeBranchData.isNeedtoSyncDB !== undefined ? (completeBranchData.isNeedtoSyncDB || completeBranchData.IsNeedtoSyncDB) : false,
        IsArchieve: completeBranchData.isArchieve !== undefined ? (completeBranchData.isArchieve || completeBranchData.IsArchieve) : false,
        CompNumberOfDaysToShowExpiryWarning: completeBranchData.compNumberOfDaysToShowExpiryWarning || completeBranchData.CompNumberOfDaysToShowExpiryWarning,
        CompRecoveryEmail: completeBranchData.compRecoveryEmail || completeBranchData.CompRecoveryEmail,
        CompRecoveryPhoneNumber: completeBranchData.compRecoveryPhoneNumber || completeBranchData.CompRecoveryPhoneNumber,
        CompFacebooklink: completeBranchData.compFacebooklink || completeBranchData.CompFacebooklink,
        CompInstagramLink: completeBranchData.compInstagramLink || completeBranchData.CompInstagramLink,
        CompYoutubeLink: completeBranchData.compYoutubeLink || completeBranchData.CompYoutubeLink,
        CompTikTokLink: completeBranchData.compTikTokLink || completeBranchData.CompTikTokLink,
        CompGoogleReview: completeBranchData.compGoogleReview || completeBranchData.CompGoogleReview,
        CompanySlug: completeBranchData.companySlug || completeBranchData.CompanySlug,
        Com_Guid: completeBranchData.com_Guid || completeBranchData.Com_Guid,
        AndroidAppver: completeBranchData.androidAppver || completeBranchData.AndroidAppver,
        IOSAppver: completeBranchData.iOSAppver || completeBranchData.IOSAppver,
        DailyForecastBracket: completeBranchData.dailyForecastBracket !== undefined ? (completeBranchData.dailyForecastBracket || completeBranchData.DailyForecastBracket) : false,
        IsTVApp: completeBranchData.isTVApp !== undefined ? (completeBranchData.isTVApp || completeBranchData.IsTVApp) : false,
        IsCallerID: completeBranchData.isCallerID !== undefined ? (completeBranchData.isCallerID || completeBranchData.IsCallerID) : false,
        ISMobileApp: completeBranchData.isMobileApp !== undefined ? (completeBranchData.isMobileApp || completeBranchData.ISMobileApp) : false,
        IsKitchenApp: completeBranchData.isKitchenApp !== undefined ? (completeBranchData.isKitchenApp || completeBranchData.IsKitchenApp) : false,
        ISWebApp: completeBranchData.isWebApp !== undefined ? (completeBranchData.isWebApp || completeBranchData.ISWebApp) : false,
        IsWaiterApp: completeBranchData.isWaiterApp !== undefined ? (completeBranchData.isWaiterApp || completeBranchData.IsWaiterApp) : false,
        WaiterAppMaxDevices: completeBranchData.waiterAppMaxDevices || completeBranchData.WaiterAppMaxDevices,
        WebAppExpireDate: completeBranchData.webAppExpireDate || completeBranchData.WebAppExpireDate,
        MobileAppExpireDate: completeBranchData.mobileAppExpireDate || completeBranchData.MobileAppExpireDate,
        WaiterAppExpireDate: completeBranchData.waiterAppExpireDate || completeBranchData.WaiterAppExpireDate,
      } : {
        // Fallback if completeBranchData is not available
        CompID: parseInt(branchId),
        CompName: formData.compName,
        CompDetail: formData.compDetail || "",
        CompEmail: formData.compEmail,
        CompPhone: formData.compPhone || "",
        CompFax: formData.compFax || "",
        CompIndustryType: formData.compIndustryType,
        CompCurrency: formData.compCurrency,
        CompCountry: formData.compCountry || "",
        CompCity: formData.compCity || "",
        CompAddress: formData.compAddress || "",
        CompSecCode: formData.compSecCode,
        CompType: completeBranchData.compType || completeBranchData.CompType || "",
        CompRemarks: formData.compRemarks || "",
        CompPNotificationLimit_PerDay: formData.compPNotificationLimit_PerDay || 10,
        CompLanguage: formData.compLanguage || "en",
        CompWebsite: formData.compWebsite || "",
        CompTimeZone: formData.compTimeZone || "",
        CompUTCTimeZone: formData.compUTCTimeZone || "",
        CompPublish: formData.compPublish !== undefined ? formData.compPublish : true,
      };

      const response = await axiosInstance.put(
        `${APIs.PUT_UPDATE_BRANCH}/${branchId}`,
        payload
      );

      if (response.data && response.data.status) {
        showSuccessToast("Branch updated successfully!");
        navigate("/branches");
      } else {
        showErrorToast(response.data?.message || "Failed to update branch");
      }
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to update branch");
    } finally {
      setLoading(false);
    }
  };

  const renderBreadcrumb = () => (
    <div className="d-flex align-items-center mb-3">
      <div>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a href="#/">Home</a>
          </li>
          <li className="breadcrumb-item">
            <Link to="/setting">Settings</Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/branches">Branch Management</Link>
          </li>
          <li className="breadcrumb-item active">Update Branch</li>
        </ol>
        <h1 className="page-header mb-0">Update Branch</h1>
      </div>
      <div className="ms-auto">
        <button
          onClick={handleSubmit}
          className="btn btn-rounded px-4 rounded-pill"
          disabled={loading}
          style={{
            backgroundColor: themeColor,
            background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
            border: 'none',
            color: 'white',
            borderRadius: '8px',
            padding: '8px 16px',
            fontSize: '14px',
            fontWeight: '600',
            transition: 'all 0.3s ease',
            boxShadow: `0 2px 8px ${themeColor}40`
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.target.style.background = `linear-gradient(135deg, ${themeColor}dd 0%, ${themeColor} 100%)`;
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
            }
          }}
          onMouseLeave={(e) => {
            if (!loading) {
              e.target.style.background = `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`;
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
            }
          }}
        >
          {loading ? "Updating..." : "Update Branch"}
        </button>
      </div>
    </div>
  );

  if (fetching) {
    return (
      <div className="container-fluid p-3">
        <div className="text-center py-5">
          <Preloader />
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-3">
      {loading && <Preloader />}
      {renderBreadcrumb()}

        <Panel className="mb-4">
          <PanelHeader
            style={{
              backgroundColor: themeColor,
              color: 'white',
              border: 'none',
              borderRadius: '10px 10px 0 0',
              padding: '15px 20px',
              fontWeight: '600',
              fontSize: '16px'
            }}
          >
            <i className="fas fa-info-circle me-2"></i>
            General Information
          </PanelHeader>
          <PanelBody
            style={{
              padding: '25px',
              backgroundColor: isDarkMode ? '#2d3436' : '#ffffff',
              borderRadius: '0 0 10px 10px',
              border: `1px solid ${themeColor}20`
            }}
          >
            <div className="row g-4">
              <div className="col-md-6">
                <FormInput
                  label="Branch Name"
                  name="compName"
                  value={formData.compName}
                  onchange={handleInputChange("compName")}
                  required
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Email"
                  name="compEmail"
                  type="email"
                  value={formData.compEmail}
                  onchange={handleInputChange("compEmail")}
                  required
                  disabled
                />
                <small className="text-muted" style={{ color: isDarkMode ? '#b2bec3' : '#6c757d' }}>
                  Email cannot be changed
                </small>
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Phone Number"
                  name="compPhone"
                  value={formData.compPhone}
                  onchange={handleInputChange("compPhone")}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Fax Number"
                  name="compFax"
                  value={formData.compFax}
                  onchange={handleInputChange("compFax")}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                  Industry Type
                </label>
                <Select
                  options={industryTypes}
                  value={industryTypes.find((i) => i.value === formData.compIndustryType)}
                  onChange={(option) => handleSelectChange(option, "compIndustryType")}
                  styles={dropdownStyles}
                  placeholder="Select Industry Type"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                  Currency
                </label>
                <Select
                  options={currencies}
                  value={currencies.find((c) => c.value === formData.compCurrency)}
                  onChange={(option) => handleSelectChange(option, "compCurrency")}
                  styles={dropdownStyles}
                  placeholder="Select Currency"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <div className="col-md-12">
                <CountryCitySelector
                  onChange={handleCountryCityChange}
                  selectedCountry={formData.compCountry}
                  selectedCity={formData.compCity}
                  countryFieldName="compCountry"
                  cityFieldName="compCity"
                  isDarkMode={isDarkMode}
                  setLoading={setLoading}
                  selectedValueIsLabel={true}
                />
              </div>
              <div className="col-md-12">
                <FormInput
                  label="Address"
                  name="compAddress"
                  value={formData.compAddress}
                  onchange={handleInputChange("compAddress")}
                />
              </div>
              <div className="col-md-6">
                <label className="form-label fw-medium" style={{ color: isDarkMode ? '#ffffff' : '#000000' }}>
                  Time Zone <span className="text-danger">*</span>
                </label>
                <Select
                  options={timeZones.map((tz) => ({
                    value: tz.value,
                    label: tz.text,
                  }))}
                  value={timeZones
                    .filter((tz) => tz.value === formData.compTimeZone)
                    .map((tz) => ({ value: tz.value, label: tz.text }))[0] || null}
                  onChange={(option) => handleSelectChange(option, "compTimeZone")}
                  styles={dropdownStyles}
                  placeholder="Select Time Zone"
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable={false}
                />
              </div>
              <div className="col-md-6">
                <FormInput
                  label="Binding Code"
                  name="compSecCode"
                  value={formData.compSecCode}
                  onchange={handleInputChange("compSecCode")}
                  required
                />
              </div>
              <div className="col-md-12">
                <FormInput
                  label="Company Detail"
                  name="compDetail"
                  type="textarea"
                  rows={3}
                  value={formData.compDetail}
                  onchange={handleInputChange("compDetail")}
                />
              </div>
              <div className="col-md-12">
                <FormInput
                  label="Remarks"
                  name="compRemarks"
                  type="textarea"
                  rows={2}
                  value={formData.compRemarks}
                  onchange={handleInputChange("compRemarks")}
                />
              </div>
              <div className="col-md-12">
                <div className="d-flex align-items-center p-3 rounded"
                  style={{
                    backgroundColor: isDarkMode ? '#3c4043' : '#f8f9fa',
                    border: `1px solid ${themeColor}20`,
                    transition: 'all 0.3s ease'
                  }}
                >
                  <ToggleSwitch
                    isChecked={formData.compPublish}
                    onToggle={(checked) => setFormData((prev) => ({ ...prev, compPublish: checked }))}
                    label="Active Branch"
                    name="compPublish"
                    themeColor={themeColor}
                  />
                </div>
              </div>
            </div>
          </PanelBody>
        </Panel>

    </div>
  );
};

export default UpdateBranch;
